import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileDown, X, ExternalLink, AlertTriangle, Sparkles } from 'lucide-react';
import { exportBoardReportHtml } from '../api';
import { useToast } from '../components/ui/ToastProvider';

const BoardReportCtx = createContext(null);

export function useBoardReport() {
  const ctx = useContext(BoardReportCtx);
  if (!ctx) throw new Error('useBoardReport must be used within BoardReportProvider');
  return ctx;
}

export function BoardReportProvider({ children }) {
  const { push } = useToast();
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('');
  const [blobUrl, setBlobUrl] = useState('');
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const timersRef = useRef([]);
  const blobUrlRef = useRef('');

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const revokeBlob = () => {
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch (_) { /* ignore */ }
      blobUrlRef.current = '';
    }
  };

  const generate = useCallback(async (ids, { includeOpen = false } = {}) => {
    if (!Array.isArray(ids) || ids.length < 2) {
      push({ type: 'warning', message: 'Välj minst 2 revisioner för LG/Styrelserapport.' });
      return;
    }
    setModalOpen(true);
    setError('');
    revokeBlob();
    setBlobUrl('');
    setLoading(true);
    setStage('Samlar in revisionsdata...');
    clearTimers();
    const stages = [
      { delay: 5000, text: 'KanAI analyserar tvärgående mönster...' },
      { delay: 30000, text: 'Bygger riskmatris och heatmap...' },
      { delay: 60000, text: 'Genererar LG/Styrelserapport...' },
      { delay: 100000, text: 'Renderar visuell rapport...' },
    ];
    timersRef.current = stages.map(s => setTimeout(() => setStage(s.text), s.delay));
    try {
      const blob = await exportBoardReportHtml(ids, includeOpen);
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setBlobUrl(url);
      setModalOpen(true); // Återoppna om användaren hann stänga
      push({ type: 'success', message: 'LG/Styrelserapport klar.' });
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : '';
      if (e && e.name === 'AbortError') {
        setError('Rapporten tog för lång tid att generera. Försök igen, eller välj färre revisioner.');
        push({ type: 'error', message: 'LG/Styrelserapporten tog för lång tid.' });
      } else if (msg.includes('Inga stängda revisioner')) {
        setError('Inga stängda revisioner i urvalet. Kryssa i "Inkludera pågående" för att använda preliminärrapporter som underlag.');
        push({ type: 'warning', message: 'Inga stängda revisioner i urvalet.' });
      } else {
        setError('Kunde inte generera LG/Styrelserapport. Försök igen.');
        push({ type: 'error', message: 'Kunde inte generera LG/Styrelserapport. Försök igen.' });
      }
      setModalOpen(true);
    } finally {
      clearTimers();
      setLoading(false);
      setStage('');
    }
  }, [push]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    // Om rapporten fortfarande genereras: behåll state så modalen kan återoppnas när den är klar.
    if (loading) return;
    setError('');
    revokeBlob();
    setBlobUrl('');
  }, [loading]);

  const openModal = useCallback(() => {
    if (loading || blobUrl || error) setModalOpen(true);
  }, [loading, blobUrl, error]);

  const value = {
    loading,
    stage,
    blobUrl,
    error,
    modalOpen,
    generate,
    closeModal,
    openModal,
  };

  return (
    <BoardReportCtx.Provider value={value}>
      {children}
      {modalOpen && createPortal(<BoardReportModal />, document.body)}
    </BoardReportCtx.Provider>
  );
}

function BoardReportModal() {
  const { loading, stage, blobUrl, error, closeModal } = useBoardReport();
  const stageText = stage || 'Förbereder…';
  const stageKey = stageText.toLowerCase();
  let activeStep = 0;
  if (stageKey.includes('analyser')) activeStep = 1;
  else if (stageKey.includes('riskmatris') || stageKey.includes('heatmap') || stageKey.includes('genererar styrelse') || stageKey.includes('sammanfat') || stageKey.includes('insikt')) activeStep = 2;
  else if (stageKey.includes('renderar') || stageKey.includes('formaterar') || stageKey.includes('utskrift')) activeStep = 3;
  const steps = [
    'Hämtar revisionsdata',
    'Analyserar avvikelser och mönster',
    'Genererar sammanfattning och insikter',
    'Formaterar rapport för utskrift',
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000 }} role="dialog" aria-modal="true">
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)' }} onClick={closeModal} aria-hidden="true" />
      <div style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
        width: '94vw', maxWidth: 1100, height: '90vh', borderRadius: 14,
        border: '1px solid #E2E8F0', background: '#FFF', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 30px rgba(30,41,59,0.18)', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 32, height: 32, borderRadius: 8, background: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileDown style={{ width: 18, height: 18, color: '#7C3AED', strokeWidth: 2.5 }} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>LG/Styrelserapport</div>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 1 }}>
                {loading ? 'Genererar rapport…' : (blobUrl ? 'Rapport klar — förhandsgranskning nedan' : (error ? 'Kunde inte generera rapport' : ''))}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {blobUrl && !loading && (
              <>
                <button
                  onClick={() => { try { window.open(blobUrl, '_blank', 'noopener'); } catch (_) { /* ignore */ } }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #E2E8F0', background: '#FFF', color: '#334155', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <ExternalLink size={14} /> Öppna i ny flik
                </button>
                <button
                  onClick={() => {
                    const iframe = document.getElementById('board-report-iframe');
                    try { iframe && iframe.contentWindow && iframe.contentWindow.print(); } catch (_) { /* ignore */ }
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', background: 'var(--kp-accent-fill, #2563EB)', color: '#FFF', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <FileDown size={14} /> Skriv ut / Spara PDF
                </button>
              </>
            )}
            <button onClick={closeModal} style={{
              width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B',
            }} aria-label="Stäng"><X size={18} strokeWidth={2.5} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#F1F3F6' }}>
          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
              <div style={{ width: '100%', maxWidth: 520, background: '#FFF', border: '1px solid #E2E8F0', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(15,23,42,0.04)', padding: '40px 36px 32px', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F5F3FF', border: '1px solid #DDD6FE', color: '#7C3AED', fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 999, marginBottom: 20, letterSpacing: '0.02em' }}>
                  <Sparkles size={12} /> KanAI
                </div>
                <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 24px' }}>
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(124,58,237,0.18)', animation: 'kp-orb-pulse 2.4s cubic-bezier(0.4,0,0.6,1) infinite' }} />
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid rgba(124,58,237,0.18)', animation: 'kp-orb-pulse 2.4s cubic-bezier(0.4,0,0.6,1) infinite', animationDelay: '0.8s' }} />
                  <div style={{ position: 'absolute', inset: 14, borderRadius: '50%', background: 'linear-gradient(135deg, #7C3AED 0%, #9333EA 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}>
                    <Sparkles style={{ width: 22, height: 22, color: '#FFF', animation: 'kp-orb-sparkle 2s ease-in-out infinite' }} />
                  </div>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: '#1E293B', margin: 0, marginBottom: 8, letterSpacing: '-0.01em' }}>
                  Genererar LG/Styrelserapport
                </h2>
                <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.5, margin: 0, marginBottom: 28 }}>
                  KanAI analyserar valda revisioner och sammanställer rapporten. Detta tar vanligtvis 1–2 minuter, längre med många revisioner. Du kan stänga rutan och arbeta vidare under tiden.
                </p>
                <div style={{ width: '100%', height: 6, background: '#F3E8FF', borderRadius: 999, overflow: 'hidden', marginBottom: 24, position: 'relative' }}>
                  <div style={{ height: '100%', width: '30%', borderRadius: 999, background: 'linear-gradient(90deg, #7C3AED 0%, #A855F7 50%, #7C3AED 100%)', backgroundSize: '200% 100%', animation: 'kp-progress-indet 2s ease-in-out infinite, kp-progress-shimmer 2s linear infinite' }} />
                </div>
                <div style={{ textAlign: 'left', borderTop: '1px solid #E4E9F0', paddingTop: 20 }}>
                  {steps.map((label, i) => {
                    const isDone = i < activeStep;
                    const isActive = i === activeStep;
                    const color = isActive ? '#1E293B' : (isDone ? '#475569' : '#7E8FA3');
                    const fontWeight = isActive ? 500 : 400;
                    const borderColor = isActive ? '#7C3AED' : (isDone ? '#059669' : '#E2E8F0');
                    const bg = isActive ? '#7C3AED' : (isDone ? '#059669' : '#FFF');
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', fontSize: 13, color, fontWeight, transition: 'color 150ms ease' }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1.5px solid ${borderColor}`, background: bg, transition: 'all 150ms ease' }}>
                          {isDone && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                          {isActive && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'kp-spin 0.8s linear infinite' }}>
                              <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                              <path d="M21 12a9 9 0 0 1-9 9" stroke="#FFF" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                          )}
                        </div>
                        <span style={{ flex: 1 }}>{label}</span>
                      </div>
                    );
                  })}
                </div>
                <p style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #E4E9F0', fontSize: 12, color: '#8D9DB0', lineHeight: 1.5 }}>
                  <strong style={{ color: '#64748B', fontWeight: 500 }}>Tips:</strong> Rapporten visas automatiskt här när den är klar. Du kan stänga rutan, byta modul och fortsätta arbeta — genereringen fortsätter i bakgrunden och modalen öppnas igen när den är klar.
                </p>
              </div>
              <style>{`
                @keyframes kp-spin { to { transform: rotate(360deg); } }
                @keyframes kp-orb-pulse { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(1.6); opacity: 0; } }
                @keyframes kp-orb-sparkle { 0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; } 50% { transform: scale(1.08) rotate(180deg); opacity: 0.85; } }
                @keyframes kp-progress-indet { 0% { margin-left: -30%; width: 30%; } 50% { margin-left: 50%; width: 40%; } 100% { margin-left: 100%; width: 30%; } }
                @keyframes kp-progress-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
              `}</style>
            </div>
          )}
          {!loading && error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
              <AlertTriangle style={{ width: 40, height: 40, color: '#DC2626' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>Kunde inte generera rapport</div>
              <div style={{ fontSize: 13, color: '#64748B', maxWidth: 520 }}>{error}</div>
              <button
                onClick={closeModal}
                style={{ marginTop: 8, padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: '1px solid #E2E8F0', background: '#FFF', color: '#334155', cursor: 'pointer', fontFamily: 'inherit' }}
              >Stäng</button>
            </div>
          )}
          {!loading && blobUrl && (
            <iframe
              id="board-report-iframe"
              title="LG/Styrelserapport"
              src={blobUrl}
              style={{ width: '100%', height: '100%', border: 'none', background: '#FFF' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
